<template>
  <div>
    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/waivers/types">Waiver types</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ capitalizeFirstLetter(selectedWaiverConfig.type) }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <!--    Fonts-->
    <div v-for="(f, i) in fonts" :key="'C' + i">
      <link rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + f.family">
    </div>

    <div class="row">
      <div :class="leftColClass">
        <card>
          <tab-rail
            :tabs="categories"
            :value="activeTabIndex"
            @input="switchTab"
            class="mb-3"
          >
            <template #actions>
              <badge v-if="selectedWaiverConfig.is_active === 1" type="success">Active</badge>
              <badge v-if="selectedWaiverConfig.is_active === 0" type="danger">Inactive</badge>
              <el-tooltip content="More actions"
                          effect="light"
                          :open-delay="300"
                          placement="top">
                <el-dropdown>
                  <base-button
                    link
                    type="primary"
                    size="sm"
                  > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-2" v-if="selectedWaiverConfig.is_active" @click="activateWaiverTemplate(0)" href="#">
                        <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="inactive-dot mr-2"></span> Deactivate waiver
                      </a>
                      <a class="dropdown-item mt-2" v-if="!selectedWaiverConfig.is_active" @click="activateWaiverTemplate(1)" href="#">
                        <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="active-dot mr-2"></span> Activate waiver
                      </a>
                      <hr v-if="selectedWaiverConfig.type !== 'shareable'">
                      <a v-if="selectedWaiverConfig.type !== 'shareable'" class="dropdown-item mb-2" @click="visitPortal()" href="#">
                        <i class="tim-icons text-info icon-link-72 mr-2"></i> Visit waiver portal
                      </a>
                      <a v-if="selectedWaiverConfig.type === 'traditional'" class="dropdown-item mb-2" @click="visitPortal('/kiosk')" href="#">
                        <i class="tim-icons text-default icon-tablet-2 mr-2"></i> Visit kiosk
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </el-tooltip>
            </template>
          </tab-rail>
          <!--          General-->
          <div v-if="activeTabIndex === 0">
            <form-section title="Waiver type">
              <p class="waiver-type-value mb-0">{{ capitalizeFirstLetter(selectedWaiverConfig.type) }} waiver</p>
            </form-section>

            <form-section title="Content">
              <div class="row">
                <div class="col-md-12 col-lg-12 col-sm-12 col-12">
                  <label class="form-label">
                    Header message
                    <el-popover trigger="click" placement="right">
                      <div class="popover-body">Appears at the top of the waiver page.</div>
                      <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                    </el-popover>
                  </label>
                  <base-input
                    type="text"
                    placeholder="Please review and sign before your visit"
                    v-model="selectedWaiverConfig.main_message"></base-input>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-4">
                  <label for="waiver-source" class="form-label">
                    Waiver source
                    <el-popover trigger="click" placement="right">
                      <div class="popover-body">Choose whether to show your own waiver text or link to an external URL.</div>
                      <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                    </el-popover>
                  </label>
                  <el-select
                    id="waiver-source"
                    v-model="waiverOption"
                    class="select-primary"
                  >
                    <el-option
                      v-for="option in waiverOptions"
                      class="select-primary"
                      :label="option"
                      :value="option"
                      :key="option"
                    ></el-option>
                  </el-select>
                </div>
              </div>

              <div v-if="waiverOption == 'Text'" class="mt-3">
                <div class="collapsible-header" @click="waiverContentExpanded = !waiverContentExpanded">
                  <span>Waiver text</span>
                  <i class="tim-icons" :class="waiverContentExpanded ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
                </div>
                <div v-show="waiverContentExpanded" class="mt-2">
                  <div class="d-flex justify-content-end mb-1">
                    <base-button link type="primary" size="sm" v-on:click="fillDefaultWaiver()">
                      Insert default text
                    </base-button>
                  </div>
                  <rich-text-editor
                    v-model="selectedWaiverConfig.waiver_text"
                    :height="500"
                    toolbar="basic"
                    placeholder="Enter waiver text..."
                  />
                </div>
              </div>

              <div v-if="waiverOption == 'External link'" class="row mt-3">
                <div class="col-md-12">
                  <base-input
                    label="Link to your waiver"
                    placeholder="https://example.com"
                    v-model="selectedWaiverConfig.external_link"></base-input>
                </div>
              </div>
            </form-section>

            <form-section title="Check-in behavior">
              <div class="row" v-if="selectedWaiverConfig.type === 'traditional'">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.hide_past_slot" />
                    <label class="switch-label">Hide past slots on the current day
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Customers won't see slots that have already passed today.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>

                  <div class="nested-control mt-2 mb-2" v-if="selectedWaiverConfig.hide_past_slot">
                    <segmented-toggle
                      size="sm"
                      v-model="selectedWaiverConfig.past_slot_mode"
                      :options="pastSlotModeOptions"
                    />
                  </div>
                </div>
              </div>

              <div class="row mt-2" v-if="selectedWaiverConfig.type === 'traditional'">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.multiple_games" />
                    <label class="switch-label">Allow check-in to multiple games per session
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Customers can sign in to more than one game on the same visit.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-2" v-if="selectedWaiverConfig.type === 'traditional'">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.show_booked_name_on_slots" />
                    <label class="switch-label">Show who booked each time slot
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Booked time slots show the booker's first name and last initial so guests pick the correct slot. Names are visible to anyone who can open this waiver.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.show_language_switcher" />
                    <label class="switch-label">Display language switcher
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Guests can change the waiver language. Leave off for kiosk setups that should stay in one language.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.show_participation_field" />
                    <label class="switch-label">Allow adults to sign for minors without participating
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">A participation checkbox appears on the waiver form.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>

                  <div class="nested-control mt-2 mb-2" v-if="selectedWaiverConfig.show_participation_field">
                    <label class="form-label">
                      Participation question text
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Shown to customers when they choose to sign without participating.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                    <base-input
                      placeholder="Are you participating in the event today?"
                      v-model="selectedWaiverConfig.participation_field_text"></base-input>
                  </div>
                </div>
              </div>

              <div class="row mt-2" v-if="selectedWaiverConfig.type !== 'shareable'">
                <div class="col">
                  <div class="switch-row">
                    <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.allow_easy_checkin" />
                    <label class="switch-label">Easy check-in for returning customers
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">Returning customers enter their email to reuse their previous waiver details. They can update any field that has changed. If signatures are enabled, they're always collected fresh.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col">
                  <div>
                    <label class="form-label">
                      Birthday input style
                      <el-popover trigger="click" placement="right">
                        <div class="popover-body">How customers enter the birthday on this waiver. Applies to the birthday and child birthday fields.</div>
                        <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                      </el-popover>
                    </label>
                  </div>
                  <segmented-toggle
                    class="mt-1"
                    variant="pills"
                    size="sm"
                    v-model="selectedWaiverConfig.birthday_input_style"
                    :options="birthdayInputStyleOptions"
                  />
                  <small class="d-block mt-1 text-white">{{ birthdayStyleDescription }}</small>
                </div>
              </div>
            </form-section>
          </div>

          <!--          Appearance-->
          <div v-if="activeTabIndex === 1">
            <form-section title="Colors and typography">
              <div class="color-palette">
                <section class="color-palette__group">
                  <header class="color-palette__group-head">
                    <span class="color-palette__eyebrow">Page</span>
                  </header>
                  <div class="color-palette__fields">
                    <color-field v-model="selectedWaiverConfig.background_color" label="Background" />
                    <color-field v-model="selectedWaiverConfig.text_color" label="Text" />
                  </div>
                </section>

                <section class="color-palette__group">
                  <header class="color-palette__group-head">
                    <span class="color-palette__eyebrow">Cards</span>
                  </header>
                  <div class="color-palette__fields">
                    <color-field v-model="selectedWaiverConfig.card_color" label="Card background" />
                    <color-field v-model="selectedWaiverConfig.text_color_card" label="Card text" />
                    <color-field v-model="selectedWaiverConfig.link_color" label="Links" />
                    <color-field v-model="selectedWaiverConfig.button_color" label="Buttons" />
                    <color-field v-model="selectedWaiverConfig.input_border_color" label="Input border" />
                  </div>
                </section>

                <section class="color-palette__group">
                  <header class="color-palette__group-head">
                    <span class="color-palette__eyebrow">Typography</span>
                  </header>
                  <div class="color-palette__fields">
                    <base-input label="Page font">
                      <el-select
                        v-model="selectedWaiverConfig.font_family"
                        class="select-primary"
                        :style="{'font-family': selectedWaiverConfig.font_family}"
                        name="font"
                        placeholder="Font"
                      >
                        <el-option
                          v-for="(f, i) in orderedFonts" :key="'B' + i"
                          :style="{'font-family': f.family}"
                          class="select-primary"
                          :label="f.family"
                          :value="f.family"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                    <base-input label="Card font">
                      <el-select
                        v-model="selectedWaiverConfig.font_family_card"
                        class="select-primary"
                        :style="{'font-family': selectedWaiverConfig.font_family_card}"
                        name="font"
                        placeholder="Font"
                      >
                        <el-option
                          v-for="(f, i) in orderedFonts" :key="'C' + i"
                          :style="{'font-family': f.family}"
                          class="select-primary"
                          :label="f.family"
                          :value="f.family"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>
                </section>
              </div>
              <div class="color-palette__footer">
                <a href="#" class="color-palette__reset" @click.prevent="resetPaletteColors">
                  <i class="tim-icons icon-refresh-01"></i>
                  Reset to defaults
                </a>
              </div>
            </form-section>

            <form-section title="Logo">
              <template #actions>
                <el-popover trigger="click" placement="left">
                  <div class="popover-body">Recommended resolution sizes:
                    <br/> Aspect Ratio: <b>16:9</b>
                    <br/> Pixels: <b>640x360</b>
                  </div>
                  <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                </el-popover>
              </template>
              <div class="row">
                <div class="col">
                  <div class="thumbnail">
                    <img :height="selectedWaiverConfig.logo_height" :width="selectedWaiverConfig.logo_width" :style="{'object-fit': selectedWaiverConfig.logo_fit}" :src="selectedWaiverConfig.logo" alt="preview" />
                  </div>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col">
                  <span class="btn btn-primary btn-sm btn-simple btn-file">Upload
                    <input
                      accept="image/*"
                      @change="handlePreview"
                      type="file"
                      name="..."
                      class="valid"
                      :multiple="false"
                      aria-invalid="false"
                    />
                  </span>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-lg-2">
                  <base-input v-model="selectedWaiverConfig.logo_height" type="number" label="Logo height" placeholder="360"></base-input>
                </div>
                <div class="col-lg-2">
                  <base-input v-model="selectedWaiverConfig.logo_width" type="number" label="Logo width" placeholder="640"></base-input>
                </div>
                <div class="col-lg-2">
                  <base-input label="Logo fit">
                    <el-select
                      v-model="selectedWaiverConfig.logo_fit"
                      class="select-primary"
                    >
                      <el-option
                        v-for="option in fitOptions"
                        class="select-primary"
                        :label="option"
                        :value="option"
                        :key="option"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
            </form-section>
          </div>

          <!--          Waiver fields-->
          <div v-if="activeTabIndex === 2">
            <div class="section-options mb-4">
              <div class="switch-row">
                <el-switch active-color="#e14eca" v-model="selectedWaiverConfig.display_section_titles" />
                <label class="switch-label">Display section titles on the customer waiver</label>
              </div>
              <div class="switch-row mt-2" v-if="selectedWaiverConfig.type === 'traditional'">
                <el-switch active-color="#e14eca"
                           v-model="selectedWaiverConfig.is_compact"
                           @change="modifySections(selectedWaiverConfig.is_compact)" />
                <label class="switch-label">Show time slots and games on the same step of the check-in process</label>
              </div>
            </div>

            <info-hint>Built-in fields grouped by the section they appear in on the customer waiver. Toggle each one on or off, and set whether it is required.</info-hint>

            <info-hint v-if="!hasLibraryFields && hasPermissions('custom_fields_configure')">
              Custom fields aren't configured yet. <router-link to="/settings/custom-fields">Create one in Settings</router-link> to add custom questions to this waiver.
            </info-hint>
            <info-hint v-else-if="!hasLibraryFields">
              Custom fields aren't configured yet. Ask an admin to set them up under Settings &rarr; Custom fields.
            </info-hint>

            <div v-for="(section, sectionIndex) in sections" :key="'S' + section.id" class="section-group">
              <div class="section-header">
                <span class="section-eyebrow">Section {{ sectionIndex + 1 }}</span>
                <input v-model="section.name"
                       class="section-name-input"
                       placeholder="Section name" />
              </div>
              <div v-if="Number(section.position) === 4" class="mt-2 mb-3">
                <label class="form-label">
                  Section intro text
                  <el-popover trigger="click" placement="right">
                    <div class="popover-body">Shown above the yes and no buttons before this section's fields appear. Leave blank to use the default text, which is translated into the customer's language.</div>
                    <i slot="reference" class="tim-icons el-icon-info info-icon"></i>
                  </el-popover>
                </label>
                <base-input class="mb-0"
                  placeholder="Are you accompanying children?"
                  maxlength="255"
                  v-model="selectedWaiverConfig.accompanying_children_text"></base-input>
              </div>
              <div v-if="fieldsForSection(section).length || linksForSection(section).length" class="field-list">
                <div v-for="field in fieldsForSection(section)" :key="'F' + field.id" class="field-card">
                  <div class="field-card__body">
                    <div class="field-card__type-icon">
                      <i :class="getFieldIcon(field.name)"></i>
                    </div>
                    <div class="field-card__info">
                      <div class="field-card__label text-white">
                        {{ getFieldDisplayName(field.name) }}
                        <el-tooltip v-if="getFieldTooltip(field.name)"
                                    :content="getFieldTooltip(field.name)"
                                    effect="light"
                                    :open-delay="300"
                                    placement="right">
                          <i class="tim-icons icon-alert-circle-exc field-card__hint"></i>
                        </el-tooltip>
                      </div>
                      <div class="field-card__meta">
                        <span class="field-card__type-name">{{ getFieldType(field.name) }}</span>
                        <span v-if="isFieldLocked(field.name)" class="field-card__meta-detail">
                          <i class="tim-icons icon-lock-circle mr-1"></i>Always required
                        </span>
                        <span v-if="isOptInField(field.name) && field.is_active"
                              class="field-card__meta-detail consent-mode-pill"
                              :class="{ 'consent-mode-pill--optin': field.is_optin }">
                          {{ field.is_optin ? 'Opt-in' : 'Opt-out' }}
                        </span>
                        <a v-if="isOptInField(field.name) && field.is_active"
                           href="#"
                           class="field-card__meta-link"
                           @click.prevent="openConsentDrawer(field)">
                          <i class="tim-icons icon-settings-gear-63 mr-1"></i>Advanced settings
                        </a>
                      </div>
                    </div>
                    <div v-if="!isFieldLocked(field.name)" class="field-card__actions">
                      <div class="field-card__toggle">
                        <span class="field-card__toggle-label">Active</span>
                        <el-switch active-color="#e14eca"
                                   v-model="field.is_active"
                                   @change="setActive(field)" />
                      </div>
                      <div v-if="!isOptInField(field.name)" class="field-card__toggle">
                        <span class="field-card__toggle-label">Required</span>
                        <el-switch active-color="#e14eca"
                                   v-model="field.is_required"
                                   :disabled="!field.is_active" />
                      </div>
                    </div>
                  </div>
                </div>
                <div v-for="(link, lIdx) in linksForSection(section)"
                     :key="'L' + (link.link_id || ('new-' + link.custom_field_id))"
                     class="field-card">
                  <div class="field-card__body">
                    <div class="field-card__type-icon">
                      <i :class="getCustomFieldTypeIcon(link.type)"></i>
                    </div>
                    <div class="field-card__info">
                      <div class="field-card__label text-white">{{ link.label }}</div>
                      <div class="field-card__meta">
                        <span class="field-card__type-name">{{ getCustomFieldTypeName(link.type) }}</span>
                        <span v-if="link.archived" class="field-card__meta-detail field-card__meta-detail--warning">
                          <i class="tim-icons icon-alert-circle-exc mr-1"></i>Archived in library
                        </span>
                      </div>
                    </div>
                    <div class="field-card__actions">
                      <div class="field-card__toggle">
                        <span class="field-card__toggle-label">Required</span>
                        <el-switch active-color="#e14eca"
                                   :value="!!link.required"
                                   @change="val => toggleLinkRequired(link, val)" />
                      </div>
                      <el-tooltip content="Remove" effect="light" :open-delay="300" placement="top">
                        <base-button class="field-card__remove-btn" simple type="danger" link size="sm" @click="removeLink(link)">
                          <i class="tim-icons icon-simple-remove"></i>
                        </base-button>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="section-empty">{{ emptySectionMessage(section) }}</div>
              <div v-if="hasLibraryFields && canAddCustomFieldsToSection(section)" class="picker-row">
                <el-select
                  :value="null"
                  placeholder="+ Add custom field"
                  filterable
                  :popper-append-to-body="true"
                  class="add-custom-field-select"
                  @change="addLinkToSection(section, $event)">
                  <el-option
                    v-for="cf in availableForWaiver"
                    :key="'O' + section.position + '-' + cf.id"
                    :label="cf.label"
                    :value="cf.id">
                    <span>{{ cf.label }}</span>
                    <span class="add-custom-field-select__option-type">{{ getCustomFieldTypeName(cf.type) }}</span>
                  </el-option>
                </el-select>
                <div v-if="!availableForWaiver.length" class="picker-hint">All library fields are attached.</div>
              </div>
            </div>
          </div>

<!--          Embedding-->
          <div v-if="activeTabIndex === 3">
            <p>Copy the following HTML and embed it in your code to display the waiver on your website.</p>
            <div class="row mt-2">
              <div class="col-lg-6">
                <base-alert type="white" class="text-muted">

                  <div class="row">
                    <div class="col-11">
                      <code>{{ embedded_text }}</code>
                    </div>
                    <div class="col text-right">
                      <base-button class="like btn-link"
                                   type="default"
                                   size="sm" style="padding: 0px 0px 2px"
                                   v-on:click="copy()">
                        <i class="tim-icons icon-single-copy-04"></i>
                      </base-button>
                    </div>
                  </div>
                </base-alert>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <h4 slot="header" class="card-title">QR code
                  <i style="cursor: pointer" v-on:click="downloadQr()" class="tim-icons icon-cloud-download-93 text-white ml-2"></i>
                </h4>
                <div>
                  <qrcode-vue id="qr" :value="qr.value" :size="qr.size" level="H"></qrcode-vue>
                </div>
              </div>
            </div>

          </div>

          <!--    Actions-->
          <div class="row mt-3">
            <div class="col">
              <base-button type="primary" v-on:click="updateWaiverConfigurations()">Save changes</base-button>
            </div>
          </div>
        </card>
      </div>
      <div class="col-md-5" v-if="activeTabIndex === 0 && selectedWaiverConfig.type !== 'generic'">
        <card v-if="activeTabIndex === 0">
          <h4 class="card-title">Waiver page content</h4>
          <div class="row">
            <div class="col">
              <table class='table' v-if="games.length > 0">
                <thead>
                <tr>
                  <th scope='col'></th>
                  <th scope='col'>Game</th>
                  <th scope='col'>Actions</th>
                </tr>
                </thead>
                <tbody tag='tbody' v-model='games' is='draggable'>
                <tr v-on:dragend="updateSiteContent('update_order', item)"  v-for='(item, index) in games' :key="'G' + index">
                  <td scope='row'>
                    <i style="cursor: move" class="tim-icons el-icon-rank"></i>
                  </td>
                  <td scope='row'>
                    <div class="row" >
                      <div class="col">
                        <p class="text-white">{{item.name}}</p>
                        <badge v-if="item.show_on_waivers" type="success">active</badge>
                        <badge v-if="!item.show_on_waivers" type="danger">inactive</badge>
                      </div>
                    </div>
                  </td>
                  <td scope='row'>
                    <div class="row">
                      <el-dropdown>
                        <base-button
                          link
                          type="primary"
                          size="sm"
                        > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <a v-if="!item.show_on_waivers" class="dropdown-item mt-1 mb-1" target="_blank" @click.prevent="updateSiteContent('add_to_site', item)">
                              <i class="tim-icons text-default icon-simple-add mr-2"></i> Add to waiver page
                            </a>
                            <a v-if="item.show_on_waivers" class="dropdown-item mt-1 mb-1" target="_blank" @click.prevent="updateSiteContent('remove_from_site', item)">
                              <i class="tim-icons text-default icon-simple-remove mr-2"></i> Remove from waiver page
                            </a>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </card>

      </div>
    </div>

    <base-drawer :show="consentDrawer.show"
                 :title="consentDrawer.field ? getFieldDisplayName(consentDrawer.field.name) : 'Advanced settings'"
                 width="520px"
                 @close="consentDrawer.show = false">
      <template v-if="consentDrawer.field">
        <drawer-section title="Wording" icon="icon-paper">
          <info-hint>Text shown next to the checkbox on the customer-facing waiver. Leave blank to use the default.</info-hint>
          <base-input :label="'Checkbox label'" class="consent-label-input">
            <el-input v-model="consentDrawer.field.label"
                      maxlength="255"
                      show-word-limit
                      :placeholder="defaultConsentLabel(consentDrawer.field.name, consentDrawer.field.is_optin)" />
          </base-input>
        </drawer-section>

        <drawer-section title="Behavior" icon="icon-settings-gear-63">
          <info-hint>The checkbox is always rendered <strong>unchecked</strong> by default. This setting controls what checking the box means.</info-hint>
          <el-radio-group v-model="consentDrawer.field.is_optin" class="consent-radio-group">
            <el-radio :label="false" class="consent-radio">
              <span class="consent-radio__title">Opt-out</span>
              <span class="consent-radio__desc">Checking the box means the customer <strong>refuses</strong>. Suitable for the United States and other jurisdictions without explicit-consent laws.</span>
            </el-radio>
            <el-radio :label="true" class="consent-radio">
              <span class="consent-radio__title">Opt-in</span>
              <span class="consent-radio__desc">Checking the box means the customer <strong>consents</strong>. Required for Canadian (CASL/PIPEDA) and similar privacy regimes.</span>
            </el-radio>
          </el-radio-group>
        </drawer-section>

        <drawer-section title="Preview" icon="icon-image-02">
          <info-hint>How this will appear on the waiver:</info-hint>
          <div class="consent-preview">
            <span class="consent-preview__checkbox"></span>
            <span class="consent-preview__label">{{ consentDrawer.field.label || defaultConsentLabel(consentDrawer.field.name, consentDrawer.field.is_optin) }}</span>
          </div>
        </drawer-section>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="alertDialog.show"
      :title="alertDialog.title"
      :message="alertDialog.message"
      :variant="alertDialog.variant"
      :confirm-text="alertDialog.confirmText"
      @confirm="closeAlert"
      @cancel="closeAlert" />

  </div>

</template>

<script>
import { ColorPicker, Select, Option, Tooltip, Dropdown, DropdownItem, DropdownMenu } from 'element-ui';
import { BaseAlert } from 'src/components';
import { Auth } from 'aws-amplify'
import { API_LOCATION, WAIVER_URL } from '../../api-config'
const axios = require('axios')
import QrcodeVue from 'qrcode.vue'
import _ from "lodash"
import RichTextEditor from '@/components/RichTextEditor'
import Badge from  "@/components/Badge"
import ColorField from '@/components/PaymentPages/ColorField.vue'
import draggable from 'vuedraggable'
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import TabRail from '@/components/TabRail.vue'

export default {
  name: "WaiverTemplate",
  components: {
    [ColorPicker.name]: ColorPicker,
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    Breadcrumb,
    BreadcrumbItem,
    TabRail,
    Badge,
    QrcodeVue,
    BaseAlert,
    RichTextEditor,
    ColorField,
    draggable
  },
  computed: {
    leftColClass(){
      // Generic has no games panel, so its editor uses the full width on every tab.
      if(this.selectedWaiverConfig.type === 'generic') return 'col-12'
      return this.activeTabIndex === 2 ? 'col-12' : this.customColWidth
    },
    categories(){
      // Tab labels by type. TabRail drives the active state off :value, so no
      // per-item active flag is needed. Shareable has no Embedding tab.
      const tabs = [
        { name: 'General settings', shortName: 'General' },
        { name: 'Appearance', shortName: 'Appearance' },
        { name: 'Sections and fields', shortName: 'Fields' },
      ]
      if(this.selectedWaiverConfig.type !== 'shareable'){
        tabs.push({ name: 'Embedding', shortName: 'Embed' })
      }
      return tabs
    },
    birthdayStyleDescription(){
      const descriptions = {
        typed: 'The customer types the date, matching your date format setting.',
        dropdowns: 'Separate day, month and year dropdowns. Prevents format mistakes and impossible dates.',
        calendar: 'A calendar picker. Familiar, but reaching a birth year takes more clicks.'
      }
      return descriptions[this.selectedWaiverConfig.birthday_input_style] || descriptions.typed
    },
    hasLibraryFields(){
      return this.availableCustomFields.length > 0
    },
    availableForWaiver(){
      const linkedIds = new Set(this.customFieldLinks.map(l => l.custom_field_id))
      return this.availableCustomFields.filter(f => !linkedIds.has(f.id))
    },
    orderedFonts: function () {
      _.remove(this.fonts, {
        family: 'Libre Baskerville'
      });
      _.remove(this.fonts, {
        family: 'Material Icons'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Outlined'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Sharp'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Round'
      });
      _.remove(this.fonts, {
        family: 'M PLUS Rounded 1c'
      });
      return _.orderBy(this.fonts, 'family')
    },
  },
  data() {
    return {
      alertDialog: { show: false, title: '', message: '', variant: 'warning', confirmText: 'OK', onClose: null },
      companyId: localStorage.getItem('companyId'),
      group: '',
      games: [],
      customColWidth: 'col-lg-7 col-12',
      loading: false,
      fields: [],
      consentDrawer: { show: false, field: null },
      availableCustomFields: [],
      customFieldLinks: [],
      uploadUrl: '',
      fonts: '',
      sections: [],
      embedded_text: 'iframe',
      fitOptions: ['cover', 'fill', 'contain', 'scale-down', 'none'],
      waiverOption: 'None',
      waiverOptions: ['None', 'External link', 'Text'],
      selectedWaiverConfig: {
        id: '',
        logo: '',
        background_color: '',
        button_color: '',
        input_border_color: '#7d7d7d99',
        main_message: '',
        code: '',
        external_link: '',
        waiver_text: ''
      },
      file: '',
      qr: {
        value: 'https://example.com',
        size: 300
      },
      activeTabIndex: 0,
      hiddenSection: '',
      waiverContentExpanded: false,
      pastSlotModeOptions: [
        { value: 'start', label: 'Hide once start time has passed' },
        { value: 'end', label: 'Hide once end time has passed' }
      ],
      birthdayInputStyleOptions: [
        { value: 'typed', label: 'Typed' },
        { value: 'dropdowns', label: 'Dropdowns' },
        { value: 'calendar', label: 'Calendar' }
      ]
    }
  },
  methods: {
    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    resetPaletteColors() {
      this.selectedWaiverConfig.background_color = '#1e1e2f'
      this.selectedWaiverConfig.text_color = '#ffffff'
      this.selectedWaiverConfig.card_color = '#27293d'
      this.selectedWaiverConfig.text_color_card = '#ffffff'
      this.selectedWaiverConfig.link_color = '#1d8cf8'
      this.selectedWaiverConfig.button_color = '#e14eca'
      this.selectedWaiverConfig.input_border_color = '#7d7d7d99'
    },
    hasPermissions(permission) {
      const p = JSON.parse(localStorage.getItem('permissions')) || [];
      return p.includes(permission);
    },
    async updateSiteContent(action, item){
      if(action === 'add_to_site'){
        item.show_on_waivers = 1
      } else if(action === 'remove_from_site'){
        item.show_on_waivers = 0
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      const waiverSettingsId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + this.companyId + '/waiver-configurations/' + waiverSettingsId + '/content'

      let data = {
        action: action,
        item: item,
        games: this.games
      }
      axios.put(url, data, config)
        .then((res) => {
          this.loading = false
          this.getWaiverConfigurations()
          this.showAlert({ title: 'Success', message: 'Waiver page has been updated', variant: 'success' })
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.showAlert({
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            this.showAlert({
              title: 'Error',
              message: 'The feature has not been enabled as part of your subscription plan.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    modifySections(is_compact){
      if(is_compact){
        this.hiddenSection = this.sections[1]
        this.sections.splice(1, 1)
      } else {
        this.sections.splice(1, 0, this.hiddenSection)
      }
    },
    switchTab(index){
      this.activeTabIndex = index
      if(this.activeTabIndex === 1 || this.activeTabIndex === 3){
        this.customColWidth = 'col-lg-12'
      } else {
        this.customColWidth = 'col-lg-7 col-12'
      }
    },
    showAlert({ title, message, variant = 'warning', confirmText = 'OK', onClose = null }){
      this.alertDialog = { show: true, title, message, variant, confirmText, onClose }
    },
    closeAlert(){
      const onClose = this.alertDialog.onClose
      this.alertDialog.show = false
      if(typeof onClose === 'function') onClose()
    },
    fillDefaultWaiver(){
      this.selectedWaiverConfig.waiver_text = '<p style="color:black">I HEREBY ASSUME ALL OF THE RISKS OF PARTICIPATING IN ANY/ALL ACTIVITIES ASSOCIATED WITH THIS IMMERSIVE EXPERIENCE, including by way of example and not limitation, any risks that may arise from negligence or carelessness on the part of the persons or entities being released, from dangerous or defective equipment or property owned, maintained, or controlled by them, or because of their possible liability without fault.</p>' +
        '<p style="color:black">I certify that I understand this activity has potential risks including but not limited to: 1) Use of simple tools; 2) Potentially moving or lifting objects of not more than twenty pounds; 3) Mental stress and anxiety; 4) Being in a reasonably small space with up to six people; 5) Possibility of failure to complete the experience in the suggested amount of time.</p>' +
        '<p style="color:black">I have no physical or mental illness that precludes my participation in a safe manner for myself or others. I am not under the influence of drugs or alcohol which impairs my ability to maintain my safety awareness or endangers others.</p>' +
        '<p style="color:black">I acknowledge that this Accident Waiver and Release of Liability Form will be used by the organizers of the activity in which I may participate, and that it will govern my actions and responsibilities at said activity. I agree that all staff or authorized agents may, in their sole discretion, determine it is unsafe for myself or others for my participation to continue, remove me from the premises by any lawful means.</p>' +
        '<p style="color:black">In consideration of my application and permitting me to participate in this activity, I hereby take action for myself, my executors, administrators, heirs, next of kin, successors, and assigns as follows:</p>' +
        '<p style="color:black">(A) I WAIVE, RELEASE, AND DISCHARGE from any and all liability, including but not limited to, liability arising from the negligence or fault of the entities or persons released, for my death, disability, personal injury, property damage, property theft, or actions of any kind which may hereafter occur to me, THE FOLLOWING ENTITIES OR PERSONS: The directors, officers, employees, volunteers, representatives, and agents of any and all entities authorizing this activity;</p>' +
        '<p style="color:black">(B) INDEMNIFY, HOLD HARMLESS, AND PROMISE NOT TO SUE the entities or persons mentioned in this paragraph from any and all liabilities or claims made as a result of participation in this activity, whether caused by the negligence of release or otherwise.</p>' +
        '<p style="color:black">I acknowledge that the directors, officers, employees, volunteers, representatives, and agents of any authorizing entity are NOT responsible for the errors, omissions, acts, or failures to act of any party or entity conducting a specific activity on their behalf. I hereby consent to receive medical treatment which may be deemed advisable in the event of injury, accident, and/or illness during this activity.</p>' +
        '<p style="color:black">I understand while participating in this activity and engaging with the business, I may be photographed or recorded. I agree to allow my photo, video, audio, or film likeness to be used for any legitimate purpose this authorizing entity decides and assigns. The Accident Waiver and Release of Liability Form shall be construed broadly to provide a release and waiver to the maximum extent permissible under applicable law.\n' +
        'I CERTIFY THAT I HAVE READ THIS DOCUMENT AND I FULLY UNDERSTAND ITS CONTENT. I AM AWARE THAT THIS IS A RELEASE OF LIABILITY AND A CONTRACT AND I SIGN IT OF MY OWN FREE WILL.</p>' +
        '<p style="color:black">For Parents/Guardians: We agree to take FULL RESPONSIBILITY of all individuals under the age of 18. Furthermore, We will be held responsible for any violations made by individuals under the age of 18.</p>' +
        '<p style="color:black">By signing below, you are consenting to the use of your electronic signature in lieu of an original signature on paper. You have the right to request that you sign a paper copy instead. By clicking below, you are waiving that right. After consent, you may, upon written request to us, obtain a paper copy of an electronic record. No fee will be charged for such copy and no special hardware or software is required to view it. Your agreement to use an electronic signature with us for any documents will continue until such time as you notify us in writing that you no longer wish to use an electronic signature. There is no penalty for withdrawing your consent. You should always make sure that we have a current email address in order to contact you regarding any changes, if necessary.</p>'
    },
    getIframe(){
      let iframeSrc = 'https://offthecouch.io/checkin/' + this.selectedWaiverConfig.code
      if(this.selectedWaiverConfig.type === 'lookup'){
        iframeSrc += '/lookup'
      }
      if(this.selectedWaiverConfig.type === 'generic'){
        iframeSrc += '/walk-in'
      }
      this.embedded_text =  `<div id="otcContainer"></div>
                      <script>
                        let f = document.createElement('iframe');
                        f.src = '` + iframeSrc + `';
                        f.width = '100%';
                        f.style.border = 'none';
                        window.addEventListener('message', e => {
                            if (e.origin === 'https://offthecouch.io') f.style.height = e.data.height + 'px';
                          });
                        document.getElementById('otcContainer').appendChild(f);
                      <\/script>`
    },
    copy() {
      navigator.clipboard.writeText(this.embedded_text);
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getWaiverConfigurations() {
      let waiverConfigId = this.$route.query.id

      this.loading = true
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.group.id + '/waiver-settings/' + waiverConfigId
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}

      axios.get(url, config)
        .then(response => {
          localStorage.removeItem('editing')
          this.selectedWaiverConfig = response.data.config
          if (!this.selectedWaiverConfig.past_slot_mode) {
            this.selectedWaiverConfig.past_slot_mode = 'start'
          }
          if (!this.selectedWaiverConfig.input_border_color) {
            this.$set(this.selectedWaiverConfig, 'input_border_color', '#7d7d7d99')
          }
          if (this.selectedWaiverConfig.accompanying_children_text === undefined) {
            this.$set(this.selectedWaiverConfig, 'accompanying_children_text', null)
          }
          if (!this.selectedWaiverConfig.birthday_input_style) {
            this.$set(this.selectedWaiverConfig, 'birthday_input_style', 'typed')
          }
          this.selectedWaiverConfig.hide_past_slot = Boolean(Number(this.selectedWaiverConfig.hide_past_slot))
          this.selectedWaiverConfig.multiple_games = Boolean(Number(this.selectedWaiverConfig.multiple_games))
          this.selectedWaiverConfig.show_participation_field = Boolean(Number(this.selectedWaiverConfig.show_participation_field))
          this.selectedWaiverConfig.display_section_titles = Boolean(Number(this.selectedWaiverConfig.display_section_titles))
          this.selectedWaiverConfig.is_compact = Boolean(Number(this.selectedWaiverConfig.is_compact))
          this.selectedWaiverConfig.allow_easy_checkin = Boolean(Number(this.selectedWaiverConfig.allow_easy_checkin))
          this.selectedWaiverConfig.show_booked_name_on_slots = Boolean(Number(this.selectedWaiverConfig.show_booked_name_on_slots))
          this.selectedWaiverConfig.show_language_switcher = Boolean(Number(this.selectedWaiverConfig.show_language_switcher))
          this.games = response.data.games
          if(this.selectedWaiverConfig.external_link != null && this.selectedWaiverConfig.external_link != ''){
            this.waiverOption = 'External link'
          }
          else if(this.selectedWaiverConfig.waiver_text != null && this.selectedWaiverConfig.waiver_text != ''){
            this.waiverOption = 'Text'
          }
          this.fields = (response.data.fields || []).map(f => ({
            ...f,
            is_active: !!f.is_active,
            is_required: this.isOptInField(f.name) ? false : !!f.is_required,
            label: f.label || '',
            is_optin: !!f.is_optin,
          }))
          this.customFieldLinks = (response.data.custom_field_links || []).map(l => ({
            ...l,
            active: !!l.active,
            required: !!l.required,
          }))
          this.sections = response.data.sections
          this.generateQrCode()
          this.getIframe()
          if(this.selectedWaiverConfig.is_compact){
            this.modifySections(this.selectedWaiverConfig.is_compact)
          }

          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert({
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            this.showAlert({
              title: 'Error',
              message: 'The feature has not been enabled as part of your subscription plan.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })

    },
    handlePreview (){
      let file = event.target.files[0];
      this.selectedWaiverConfig.logo = URL.createObjectURL(file);
      this.file = file
      this.$emit('change', file);
    },
    async saveFile() {
      this.uploadUrl = await this.getSignedURL(this.file)
      let uploaded = await this.uploadFile(this.file)
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( (resolve) => {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            this.showAlert({ title: 'Error', message: 'You must upload a photo before saving!', variant: 'danger' })
            console.error(err)
          })
      })
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    setActive(item){
      if(!item.is_active){
        item.is_required = false
      }
    },
    isFieldLocked(name){
      return name === 'Booking Date' || name === 'Booking Time' || name === 'Email'
    },
    isOptInField(name){
      return name === 'Photo Opt Out' || name === 'Email Opt Out'
    },
    getFieldDisplayName(name){
      if(name === 'Email Opt Out') return 'Email notification preferences'
      if(name === 'Photo Opt Out') return 'Photo sharing preferences'
      return name
    },
    defaultConsentLabel(name, isOptin){
      if(name === 'Email Opt Out'){
        return isOptin
          ? 'Yes, sign me up for marketing emails'
          : 'Do not include me in mailing lists'
      }
      if(name === 'Photo Opt Out'){
        return isOptin
          ? 'I consent to my photos being shared on social media'
          : 'Do not share my photos on social media'
      }
      return ''
    },
    openConsentDrawer(field){
      this.consentDrawer.field = field
      this.consentDrawer.show = true
    },
    canAddCustomFieldsToSection(section){
      const pos = Number(section.position)
      const type = this.selectedWaiverConfig && this.selectedWaiverConfig.type
      if(type === 'traditional') return pos >= 3
      if(type === 'lookup') return pos >= 3
      return true
    },
    emptySectionMessage(section){
      const type = this.selectedWaiverConfig && this.selectedWaiverConfig.type
      const pos = Number(section.position)
      if(type === 'traditional' && pos === 1){
        return 'This section is the date selection step on the customer waiver. No fields are configurable here.'
      }
      if(type === 'traditional' && pos === 2){
        return 'This section is the time slot selection step on the customer waiver. No fields are configurable here.'
      }
      if(type === 'lookup' && pos === 1){
        return 'This section is the team leader lookup step on the customer waiver. No fields are configurable here.'
      }
      if(type === 'lookup' && pos === 2){
        return 'This section is the booking selection step on the customer waiver. No fields are configurable here.'
      }
      return 'No fields in this section yet.'
    },
    getFieldType(name){
      const map = {
        'First Name': 'INPUT FIELD',
        'Last Name': 'INPUT FIELD',
        'Email': 'EMAIL',
        'Phone': 'PHONE',
        'Birthday': 'DATE',
        'Child First Name': 'INPUT FIELD',
        'Child Last Name': 'INPUT FIELD',
        'Child Birthday': 'DATE',
        'Booking Date': 'DATE',
        'Booking Time': 'TIME',
        'Email Opt Out': 'CHECKBOX',
        'Photo Opt Out': 'CHECKBOX',
        'Source': 'DROPDOWN',
        'Experience': 'DROPDOWN',
        'Location': 'DROPDOWN',
        'Signature': 'SIGNATURE',
      }
      return map[name] || 'FIELD'
    },
    getFieldIcon(name){
      const icons = {
        'INPUT FIELD': 'tim-icons icon-pencil',
        'EMAIL': 'tim-icons icon-email-85',
        'PHONE': 'tim-icons icon-mobile',
        'DATE': 'tim-icons icon-calendar-60',
        'TIME': 'tim-icons icon-time-alarm',
        'CHECKBOX': 'far fa-check-square',
        'DROPDOWN': 'tim-icons icon-bullet-list-67',
        'SIGNATURE': 'tim-icons icon-pencil',
      }
      return icons[this.getFieldType(name)] || 'tim-icons icon-bullet-list-67'
    },
    getFieldTooltip(name){
      const tips = {
        'Source': 'Ask customers where they heard about you',
        'Location': 'Ask customers where they are from (city, state, and zip or postal code)',
        'Experience': 'Ask customers how many games they played',
      }
      return tips[name] || null
    },
    getFieldSection(name){
      // Booking Date and Booking Time are server-driven — never user-toggleable, so omit from the admin field list.
      if(name === 'Booking Date' || name === 'Booking Time') return null
      const type = this.selectedWaiverConfig && this.selectedWaiverConfig.type
      if(type === 'shareable'){
        // Section 1: "Your bookings" (read-only display). Section 2: all form fields.
        return 2
      }
      if(type === 'lookup'){
        // Section 1: lookup form. Section 2: booking selection. Section 3: all form fields.
        return 3
      }
      const map = {
        'First Name': 3,
        'Last Name': 3,
        'Email': 3,
        'Phone': 3,
        'Birthday': 3,
        'Child First Name': 4,
        'Child Last Name': 4,
        'Child Birthday': 4,
        'Signature': 5,
        'Email Opt Out': 5,
        'Photo Opt Out': 5,
        'Source': 6,
        'Experience': 6,
        'Location': 6,
      }
      return map[name] || null
    },
    fieldsForSection(section){
      const positions = [section.position]
      if(this.selectedWaiverConfig.is_compact && section.position === 1){
        positions.push(2)
      }
      const filtered = this.fields.filter(f => positions.includes(this.getFieldSection(f.name)))
      // Group consent checkboxes together at the end of the section so the admin view
      // mirrors the customer waiver, where consent prompts always follow the main fields.
      return filtered.slice().sort((a, b) => {
        const aOpt = this.isOptInField(a.name) ? 1 : 0
        const bOpt = this.isOptInField(b.name) ? 1 : 0
        return aOpt - bOpt
      })
    },
    async getLibraryCustomFields(){
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.group.id + '/custom-fields?count=500&offset=0&archived=0'
        let res = await axios.get(url, config)
        this.availableCustomFields = res.data.custom_fields || []
      } catch(err){
        console.log(err)
        this.availableCustomFields = []
      }
    },
    linksForSection(section){
      return this.customFieldLinks
        .filter(l => Number(l.waiver_section) === Number(section.position))
        .sort((a, b) => (a.position || 0) - (b.position || 0))
    },
    addLinkToSection(section, customFieldId){
      if (!customFieldId) return
      const libField = this.availableCustomFields.find(f => f.id === customFieldId)
      if (!libField) return
      const sectionLinks = this.linksForSection(section)
      const maxPosition = sectionLinks.reduce((max, l) => Math.max(max, l.position || 0), -1)
      this.customFieldLinks.push({
        link_id: null,
        custom_field_id: customFieldId,
        label: libField.label,
        type: libField.type,
        archived: 0,
        required: false,
        active: true,
        waiver_section: section.position,
        position: maxPosition + 1,
      })
    },
    removeLink(link){
      this.customFieldLinks = this.customFieldLinks.filter(
        l => l.custom_field_id !== link.custom_field_id
      )
    },
    toggleLinkRequired(link, val){
      const target = this.customFieldLinks.find(l => l.custom_field_id === link.custom_field_id)
      if (target) target.required = !!val
    },
    getCustomFieldTypeName(typeId){
      const map = { 1: 'CHECKBOX', 2: 'INPUT FIELD', 3: 'DROPDOWN' }
      return map[typeId] || 'FIELD'
    },
    getCustomFieldTypeIcon(typeId){
      const map = {
        1: 'far fa-check-square',
        2: 'tim-icons icon-pencil',
        3: 'tim-icons icon-bullet-list-67',
      }
      return map[typeId] || 'tim-icons icon-bullet-list-67'
    },
    async updateWaiverConfigurations(){

      this.loading = true
      if(this.waiverOption == 'External link'){
        this.selectedWaiverConfig.waiver_text = ''
      }
      else if(this.waiverOption == 'Text'){
        this.selectedWaiverConfig.external_link = ''
      } else {
        this.selectedWaiverConfig.waiver_text = ''
        this.selectedWaiverConfig.external_link = ''
      }
      if(this.file != ''){
        await this.saveFile()
        this.selectedWaiverConfig.logo = this.uploadUrl.split("?")[0]
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.group.id + '/waiver-settings/' + this.selectedWaiverConfig.id

      let payload = {
        'waiverConfigurations': this.selectedWaiverConfig,
        'fields': this.fields,
        'text_option': this.waiverOption,
        'sections': this.sections,
        'custom_field_links': this.customFieldLinks,
      }

      axios.put(url, payload, config)
        .then((res) => {
          this.loading = false
          this.showAlert({ title: 'Success', message: 'Waiver has been updated', variant: 'success' })

          this.getWaiverConfigurations()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.showAlert({
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            this.showAlert({
              title: 'Error',
              message: 'The feature has not been enabled as part of your subscription plan.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })

    },
    async activateWaiverTemplate(activate){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/waiver-configurations/' + this.selectedWaiverConfig.id + '/activate'

      let payload = {
        'is_active': activate
      }

      this.selectedWaiverConfig.is_active = activate

      axios.post(url, payload, config)
        .then((res) => {
          this.loading = false
          if(activate == 1){
            this.showAlert({ title: 'Success', message: 'Waiver template has been activated', variant: 'success' })
          } else {
            this.showAlert({ title: 'Success', message: 'Waiver template has been deactivated', variant: 'success' })
          }
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.showAlert({
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            this.showAlert({
              title: 'Error',
              message: 'The feature has not been enabled as part of your subscription plan.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          }
          else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    visitPortal(path){
      let url = WAIVER_URL + this.selectedWaiverConfig.code
      if(path){
        url += path
      }
      if(this.selectedWaiverConfig.type === 'lookup'){
        url += '/lookup'
      }
      if(this.selectedWaiverConfig.type === 'generic'){
        url += '/walk-in'
      }
      window.open(url, "_blank");
    },
    generateQrCode(){
      let url = WAIVER_URL + this.selectedWaiverConfig.code
      if(this.selectedWaiverConfig.type === 'lookup'){
        url += '/lookup'
      }
      if(this.selectedWaiverConfig.type === 'generic'){
        url += '/walk-in'
      }
      this.qr.value = url
    },
    downloadQr(){
      var link = document.createElement('a');
      link.download = 'qr.png';
      link.href = document.getElementsByTagName('canvas')[0].toDataURL()
      link.click();
    },
    getFonts(){
      let url = API_LOCATION + 'fonts'

      axios.get(url)
        .then(response => {
          this.fonts = response.data
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert({
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              variant: 'danger',
              onClose: () => this.$router.push('/')
            })
          } else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
  },
  mounted(){
    this.group = JSON.parse(localStorage.getItem('group'))

    this.getWaiverConfigurations()
    this.getLibraryCustomFields()
    this.getFonts()
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css?family=Nunito");

.waiver-type-value {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.form-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: rgba(255, 255, 255, 0.85);
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.switch-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  flex: 1;
  min-width: 0;
}

.nested-control {
  margin-left: 3rem;
}

.info-icon {
  color: #1d8cf8;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.375rem;
  margin: -0.375rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
}

.info-icon:hover {
  background: rgba(29, 140, 248, 0.1);
}

.popover-body {
  max-width: 240px;
  font-size: 0.8125rem;
  line-height: 1.35;
  word-break: normal;
  overflow-wrap: break-word;
  text-align: left;
}

.collapsible-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  min-height: 44px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 0.25rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.875rem;
  font-weight: 500;
  user-select: none;
}

.collapsible-header:hover {
  background: rgba(255, 255, 255, 0.07);
}

@media (max-width: 575.98px) {
  .nested-control {
    margin-left: 1.5rem;
  }
  .nested-control ::v-deep .toggle-group {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    height: auto;
  }
  .nested-control ::v-deep .toggle-option {
    height: auto;
    padding-top: 8px;
    padding-bottom: 8px;
    white-space: normal;
    text-align: left;
  }
  .nested-control ::v-deep .toggle-option:not(:last-child) {
    border-right: none;
    border-bottom: 1px solid rgba(125, 125, 125, 0.3);
  }
}

::v-deep .info-hint {
  width: fit-content;
  max-width: 100%;
}

.section-group {
  margin-bottom: 1.25rem;
}
.section-group:last-child {
  margin-bottom: 0;
}
.section-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 0.5rem;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid rgba(125, 125, 125, 0.18);
  flex-wrap: wrap;
}
.section-eyebrow {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: rgba(225, 78, 202, 0.85);
}
.section-name-input {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  width: auto;
  min-width: 220px;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.section-name-input:hover {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}
.section-name-input:focus {
  border-color: rgba(225, 78, 202, 0.6);
  background: rgba(255, 255, 255, 0.05);
}
.section-empty {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding: 0.5rem 0 0.25rem 0.25rem;
}

.field-card__meta-detail--warning {
  color: #ffb236;
}
.field-card__remove-btn {
  min-width: 32px;
  min-height: 32px;
  padding: 6px;
  margin-left: 4px;
}

.picker-row {
  margin-top: 10px;
}
.add-custom-field-select {
  width: 240px;
  max-width: 100%;
}
.add-custom-field-select ::v-deep .el-input__inner {
  height: 36px;
  line-height: 36px;
  border: 1px dashed rgba(225, 78, 202, 0.4);
  background: rgba(225, 78, 202, 0.03);
  color: rgba(225, 78, 202, 0.9);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding-left: 14px;
  transition: background 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}
.add-custom-field-select ::v-deep .el-input__inner:hover {
  border-color: rgba(225, 78, 202, 0.7);
  background: rgba(225, 78, 202, 0.08);
}
.add-custom-field-select ::v-deep .el-input--suffix.is-focus .el-input__inner,
.add-custom-field-select ::v-deep .el-input__inner:focus {
  border-style: solid;
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  color: #e14eca;
}
.add-custom-field-select ::v-deep .el-input__inner::placeholder {
  color: rgba(225, 78, 202, 0.9);
  font-weight: 600;
}
.add-custom-field-select ::v-deep .el-input__icon {
  color: rgba(225, 78, 202, 0.7);
  line-height: 36px;
}
.add-custom-field-select__option-type {
  float: right;
  font-size: 10px;
  text-transform: uppercase;
  color: rgba(125, 125, 125, 0.8);
  letter-spacing: 0.04em;
  margin-left: 12px;
}
.picker-hint {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}


.field-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  transition: background 0.15s ease;
}
.field-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.field-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.field-card__info {
  flex: 1;
  min-width: 200px;
}
.field-card__type-icon {
  font-size: 1.1em;
  color: #e14eca;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}
.field-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}
.field-card__hint {
  color: #1d8cf8;
  font-size: 0.8rem;
  cursor: pointer;
}
.field-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.field-card__type-name {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
}
.field-card__meta-detail {
  display: inline-flex;
  align-items: center;
}
.field-card__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.field-card__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}
.field-card__toggle-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 600;
}
.consent-mode-pill {
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(225, 78, 202, 0.15);
  border: 1px solid rgba(225, 78, 202, 0.4);
  color: #e14eca;
}
.consent-mode-pill--optin {
  background: rgba(46, 204, 113, 0.15);
  border-color: rgba(46, 204, 113, 0.5);
  color: #2ecc71;
}
.field-card__meta-link {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #1d8cf8;
  cursor: pointer;
}
.field-card__meta-link:hover {
  color: #e14eca;
  text-decoration: none;
}
.consent-label-input ::v-deep .el-input .el-input__count,
.consent-label-input ::v-deep .el-input .el-input__count .el-input__count-inner {
  background: transparent !important;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 500;
}
.consent-label-input ::v-deep .el-input--suffix .el-input__inner {
  padding-right: 50px;
}
.white-content .consent-label-input ::v-deep .el-input .el-input__count,
.white-content .consent-label-input ::v-deep .el-input .el-input__count .el-input__count-inner {
  color: rgba(0, 0, 0, 0.45);
}
.consent-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.consent-radio {
  display: flex;
  align-items: flex-start;
  white-space: normal;
  margin: 0;
  padding: 12px;
  background: rgba(128, 128, 128, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.375rem;
  transition: border-color 120ms ease, background 120ms ease;
}
.consent-radio.is-checked {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.06);
}
.consent-radio ::v-deep .el-radio__label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: normal;
  padding-left: 8px;
}
.consent-radio__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
}
.consent-radio__desc {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}
.consent-preview {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0.375rem;
}
.consent-preview__checkbox {
  width: 16px;
  height: 16px;
  border: 1.5px solid #999;
  border-radius: 3px;
  flex-shrink: 0;
  margin-top: 2px;
  background: #fff;
}
.consent-preview__label {
  font-size: 0.875rem;
  color: #1d253b;
  line-height: 1.4;
}
.white-content .consent-radio {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.1);
}
.white-content .consent-radio.is-checked {
  background: rgba(225, 78, 202, 0.08);
  border-color: #e14eca;
}
.white-content .consent-radio__title { color: #1d253b; }
.white-content .consent-radio__desc { color: rgba(0, 0, 0, 0.65); }

@media (max-width: 575.98px) {
  .field-card {
    padding: 10px 12px;
    max-width: none;
  }
  .field-card__type-icon {
    display: none;
  }
  .field-card__actions {
    gap: 8px;
  }
  .add-custom-field-select {
    width: 100%;
  }
  .section-name-input {
    min-width: 0;
    width: 100%;
    padding-left: 0;
  }
}

</style>

<style>
.white-content .waiver-type-value {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .form-label,
.white-content .switch-label {
  color: rgba(0, 0, 0, 0.7);
}

.white-content .collapsible-header {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.85);
}

.white-content .collapsible-header:hover {
  background: rgba(0, 0, 0, 0.06);
}

.white-content .info-icon:hover {
  background: rgba(29, 140, 248, 0.08);
}

/* Sections-and-fields tab — light mode */
.white-content .field-card {
  background: rgba(0, 0, 0, 0.02);
  border-left-color: #cc5fad;
}
.white-content .field-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .field-card__label.text-white {
  color: rgba(0, 0, 0, 0.85) !important;
}
.white-content .field-card__type-icon {
  color: #cc5fad;
}
.white-content .field-card__meta-detail--warning {
  color: #c97a00;
}
.white-content .field-card__meta {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .field-card__toggle-label {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .section-empty {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .picker-hint {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .section-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}
.white-content .section-eyebrow {
  color: #cc5fad;
}
.white-content .section-name-input {
  color: rgba(0, 0, 0, 0.85);
}
.white-content .section-name-input:hover {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.12);
}
.white-content .section-name-input:focus {
  background: rgba(0, 0, 0, 0.06);
  border-color: #cc5fad;
}
.white-content .add-custom-field-select .el-input__inner {
  border-color: rgba(204, 95, 173, 0.5);
  background: rgba(204, 95, 173, 0.05);
  color: #b04a93;
}
.white-content .add-custom-field-select .el-input__inner::placeholder {
  color: #b04a93;
}
.white-content .add-custom-field-select .el-input__inner:hover {
  border-color: rgba(204, 95, 173, 0.7);
  background: rgba(204, 95, 173, 0.08);
}
.white-content .add-custom-field-select .el-input--suffix.is-focus .el-input__inner,
.white-content .add-custom-field-select .el-input__inner:focus {
  border-color: #cc5fad;
  background: rgba(204, 95, 173, 0.1);
  color: #b04a93;
}
</style>
